import { getSubscriptionData } from "./firm.js";
import { getMemberCount, getInviteCount } from "./teams.js";
import { litigationEnabled } from "./backend-api.js";

// ==================== UTILITIES FOR CHECKING SUBSCRIPTIONS ====================
//
function subscriptionActive(subscriptionData) {
  if (!subscriptionData || !subscriptionData.plan || !subscriptionData.plan.plan) return false;
  return ["trialing", "active"].includes(subscriptionData.plan.status);
}

async function canDirectDownload(firm_id) {
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  }
  if (["professional", "executive"].includes(subscriptionData.plan.plan)) {
    return true;
  } else {
    return false;
  }
}

async function canGetReminders(firm_id) {
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  }
  if (["executive"].includes(subscriptionData.plan.plan)) {
    return true;
  } else {
    return false;
  }
}

async function canLoadPrivateData(firm_id) {
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  }

  if (["executive"].includes(subscriptionData.plan.plan)) {
    return true;
  } else {
    return false;
  }
}

async function canCustomizeRules(firm_id) {  
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  }

  if (["executive"].includes(subscriptionData.plan.plan)) {
    return true;
  } else {
    return false;
  }
}

async function canUseTrademark(firm_id) {
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  }

  if (["professional", "executive"].includes(subscriptionData.plan.plan)) {
    return true;
  } else {
    return false;
  }

}

async function canUseEmails(firm_id) {
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  }

  if (["professional", "executive"].includes(subscriptionData.plan.plan)) {
    return true;
  } else {
    return false;
  }
}

async function canUseAI(firm_id) {
  
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  }

  if (["executive"].includes(subscriptionData.plan.plan)) {
    return true;
  } else {
    return false;
  }
}

async function canInviteUsers(firm_id) {
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  }
  if (["professional", "executive"].includes(subscriptionData.plan.plan)) {
    const memberCount = await getMemberCount(firm_id);
    const inviteCount = await getInviteCount(firm_id);
    if ((memberCount + inviteCount) < subscriptionData.plan.price.custom_data.user_number) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

async function canUseTeam(firm_id) {
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  } else {
    if (["professional", "executive"].includes(subscriptionData.plan.plan)) {
      return true;
    } else {
      return false;
    }
  }
}

async function canUseIDS(firm_id) {
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  } else {
    if (["executive"].includes(subscriptionData.plan.plan)) {
      return true;
    } else {
      return false;
    }
  }
}

async function canUseFamilyTree(firm_id) {
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  } else {
    if (["executive"].includes(subscriptionData.plan.plan)) {
      return true;
    } else {
      return false;
    }
  }
}

async function canUseCustomerNumber(firm_id) {
  const subscriptionData = await getSubscriptionData(firm_id);
  if (!subscriptionActive(subscriptionData)) {
    return false;
  } else {
    if (["executive", "professional"].includes(subscriptionData.plan.plan)) {
      return true;
    } else {
      return false;
    }
  }
}

// launch gate: litigation is in limited release. The allowlist lives in the
// BACKEND (src/services/enabled_features.js there) — this just probes it.
// Fails CLOSED (module hidden) on any error; only definitive answers are
// cached, per firm, for the session.
const _litEnabledCache = new Map();
async function canUseLit(firm_id) {
  if (_litEnabledCache.has(firm_id)) return _litEnabledCache.get(firm_id);
  try {
    const res = await litigationEnabled(firm_id);
    const enabled = !!res?.enabled;
    _litEnabledCache.set(firm_id, enabled);
    // shelved features ride the same probe — cache them with the module flag
    _litExtrasCache.set(firm_id, { discoveryExtraction: !!res?.discoveryExtraction });
    return enabled;
  } catch {
    return false; // transient (token not ready, offline) — don't cache
  }
}

// shelved served-response disassembly (see the backend flag) — off everywhere
// while the chat intake is evaluated. Reads the cached /enabled probe.
const _litExtrasCache = new Map();
function canUseDiscoveryExtraction(firm_id) {
  return _litExtrasCache.get(firm_id)?.discoveryExtraction === true;
}

export { 
  subscriptionActive as canUseApp, 
  canDirectDownload, 
  canLoadPrivateData, 
  canCustomizeRules, 
  canUseTrademark, 
  canUseEmails, 
  canUseAI, 
  canInviteUsers, 
  canUseTeam,
  canGetReminders,
  canUseIDS,
  canUseFamilyTree,
  canUseCustomerNumber,
  canUseLit,
  canUseDiscoveryExtraction,
};

